import React from 'react';

import { ErrorMessage } from '@eika/attention-message';
import { ButtonTypes } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { SuccessCard } from '@eika/card';
import { IconTypes } from '@eika/icon';

import piwik from '@/piwik/piwik';
import { useReopenCard } from '@/react-query/hooks/mutation/use-block-card';

import CustomerCard from '@components/customer-card/CustomerCard';
import { Card } from '@generated-types';
import { useIsHighFraudRisk } from '@hooks/use-is-high-risk';
import useText from '@hooks/use-text';
import { isSoftBlocked } from '@utils/card-utils';

interface ReopenCardProps {
    card: Card;
}

const ErrorDisplay = () => {
    const { messageErrorText } = useText();

    return (
        <div
            className="reopen-error-display"
            data-track-id={piwik.reopenCardForm.reopenCardFailure.dataTrackId}
            data-track-linkname={piwik.reopenCardForm.reopenCardFailure.dataTrackLinkname}
            id={piwik.reopenCardForm.reopenCardFailure.dataTrackId}
        >
            <ErrorMessage text={messageErrorText} icon={IconTypes.LUKK_24} />
        </div>
    );
};

const ReopenCard = ({ card }: ReopenCardProps) => {
    const { blockReopenQuestionText, blockReopenQuestionNote, formYesOpen, messageSuccessReopenCard, genericTryAgain } =
        useText();
    const {
        isPending: isReopeningCard,
        isSuccess: hasReopenedCard,
        isError: failedReopeningCard,
        mutateAsync: reopenCard,
    } = useReopenCard();
    const isHighRisk = useIsHighFraudRisk();

    const onReopenCard = async () => {
        await reopenCard({
            cardType: card.type,
            cardId: card.id,
        });
    };

    if (hasReopenedCard || !isSoftBlocked(card)) {
        return (
            <SuccessCard
                data-track-id={piwik.reopenCardForm.reopenCardSuccess.dataTrackId}
                data-track-linkname={piwik.reopenCardForm.reopenCardSuccess.dataTrackLinkname}
                title={messageSuccessReopenCard}
            />
        );
    }

    return (
        <div className="reopen-card-display">
            <div className="customer-card-reopen">
                <CustomerCard card={card} />
            </div>
            <h3 className="question">{blockReopenQuestionText}</h3>
            <p>{blockReopenQuestionNote}</p>
            {failedReopeningCard && <ErrorDisplay />}
            {!isHighRisk && (
                <ButtonWithSpinner
                    buttonType={ButtonTypes.STANDARD_POSITIVE}
                    data-track-id={piwik.reopenCardForm.reopenCard.dataTrackId}
                    data-track-linkname={piwik.reopenCardForm.reopenCard.dataTrackLinkname}
                    id={piwik.reopenCardForm.reopenCard.dataTrackId}
                    onClick={onReopenCard}
                    showSpinner={isReopeningCard}
                >
                    {failedReopeningCard ? genericTryAgain : formYesOpen}
                </ButtonWithSpinner>
            )}
        </div>
    );
};

export default ReopenCard;
